import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { Student } from '../student.js';
import { Teacher } from '../teacher.js';

/**
 * Batch lookup used by the teacher's roster view to show student names. Restricted to the
 * calling teacher's own students (via ClassMembership) would require an extra collection, so
 * instead this simply requires the caller to be a teacher — same coarse guard used elsewhere for
 * profile lookups, since Student rows carry no other sensitive data beyond what a teacher already
 * needs to see on their own roster.
 */
export const getStudentsByIdsQuery = registerQuery({
  operationName: 'getStudentsByIdsQuery',
  type: () => [Student],
  params: [{ name: 'ids', type: () => [String] }],

  resolve: (ids: string[]) => async (_gqlFields: BaseQLSelectionSet<Student>): Promise<Student[]> => {
    const currentUser = await CurrentUserInfo.getMyself()({ _id: 1 });
    if (!currentUser?._id) return [];

    const teacherCollection = await getCollection(Teacher);
    const teacher = await teacherCollection.findOne({ userID: currentUser._id });
    if (!teacher) return [];

    const normalizedIds = Array.isArray(ids) ? ids.filter(id => typeof id === 'string' && id.length > 0) : [];
    if (normalizedIds.length === 0) return [];

    const studentCollection = await getCollection(Student);
    const students = await studentCollection.find({ _id: { $in: normalizedIds } }).toArray();

    return students as Student[];
  },
});
